/**
 * The two doors, as one dialog.
 *
 * Neither navigates to `/app/evaluate/:startupId` — that is a login wall, and
 * an anonymous investor landing on "Investor access only" is the defect this
 * replaces. Both stay inside the email-capture scope, and both pass the
 * founder-safe test: they are about *this* company, they route nowhere else,
 * and the founder sees a count, never a name.
 *
 * Copy rewritten 2026-09-11 (David): the early-access disclosure comes before
 * the ask, both doors capture the email only, and every line has to pass the
 * founder test, because the founder is the one who distributes this page.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { TextInput } from '@workspace/ui/components/text-input'
import { Goal, Podcast } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useAskProfile } from '../context/ask-profile-context'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AskProfileDoorDialog() {
  const ask = useAskProfile()
  const open = ask.openDoorState
  const [email, setEmail] = useState(ask.initialEmail)
  const [submitted, setSubmitted] = useState(false)

  // Each opening is a fresh capture. Without this the confirmation from the
  // last door is what the next one shows.
  useEffect(() => {
    if (open === null) return
    setSubmitted(false)
    setEmail(ask.initialEmail)
  }, [open, ask.initialEmail])

  if (open === null) return null

  const { door, placement } = open
  const isAgentDoor = door === 'agent_follow'
  const trimmedEmail = email.trim()
  const canSubmit = EMAIL_PATTERN.test(trimmedEmail)
  const Icon = isAgentDoor ? Podcast : Goal

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return
    // Both doors capture the email only. The brief and the criteria fields
    // were removed: asking an investor to fill a form before an account
    // exists is friction in front of a fake door, and the signal that
    // matters is WHICH door they chose, which `intent` already carries.
    ask.submitDoor({ door, placement, email: trimmedEmail })
    setSubmitted(true)
  }

  return (
    <Dialog open onOpenChange={(next) => !next && ask.closeDoor()}>
      <DialogContent className="max-w-[32.5rem]">
        <DialogHeader>
          <Icon className="mb-2 size-6 text-primary" aria-hidden />
          <DialogTitle>
            {isAgentDoor
              ? `Follow ${ask.startupName}.`
              : `See how ${ask.startupName} fits your thesis.`}
          </DialogTitle>
          <DialogDescription>
            {isAgentDoor
              ? 'It reads every update to this profile and flags what matters to you: a paying customer, new round terms, a key hire.'
              : "Your AI agent reads this profile against your thesis and shows where it fits and where it doesn't."}
          </DialogDescription>
          <p className="text-[13px] leading-relaxed text-foreground/90">
            {isAgentDoor
              ? 'You set what it watches for.'
              : 'Upload your thesis once. It evaluates every profile you open against it.'}
          </p>
        </DialogHeader>

        {submitted ? (
          <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-2.5 text-[12.5px] leading-relaxed text-foreground">
            {isAgentDoor ? (
              <>
                <b className="font-semibold text-emerald-400">Following.</b> Your AI agent follows{' '}
                {ask.startupName}. The founder sees one more follower, never who. When your account
                opens, you set what it watches for.
              </>
            ) : (
              <>
                <b className="font-semibold text-emerald-400">Saved.</b> When your account opens,{' '}
                {ask.startupName} and every profile after it is evaluated against your thesis. The
                founder never sees it.
              </>
            )}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="border-l-2 border-primary pl-3 text-[12.5px] leading-relaxed text-muted-foreground">
              <b className="font-semibold text-foreground">Early access.</b> Leave your email and
              your account opens first.
            </p>

            <TextInput
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@fund.com"
              aria-label="Your email"
              autoFocus
              required
            />

            <CtaButton type="submit" disabled={!canSubmit}>
              {isAgentDoor ? 'Follow this company' : 'Evaluate this company'}
            </CtaButton>
            <p className="text-[11px] text-muted-foreground">
              Free. No card. The founder sees a count, never who.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
